import React, { createContext, useContext, useMemo, useState } from 'react';
import { changesSinceReturn, amendmentChangesSinceReturn } from './changeDiff';
import {
  SHARED_CHANGE_LABELS, fieldCommentBox, fieldCommentText, fieldCommentMeta, fieldCommentUpdated,
} from './constants';

// 退件的逐項意見——前端的單一出處（docs/workflow-ui.md §4.3）。
//
// 按下 Reject 後除了填寫原因，還可以針對某幾項做單項的意見說明。
// 審核者**不能代改表單**——單子仍然退回讓申請者自己改，意見只是註解。
// 所以這裡沒有任何寫入 API：意見是草稿，跟著那一次 Return for Changes 一起
// 送出，之後就永遠是那顆 `rejected` 事件的一部分。
//
// 兩份資料，形狀一樣但角色相反：
//   drafts   — 審核者現在正在打的字（頁面 state，重新整理就沒了）
//   existing — 上一次退件時留下的意見（來自 events，永久保存在申請單上）
//
// `field` 用的是各頁 CHANGE_LABELS 那一組 key，所以一則意見可以同時對得起
// History 的 diff 表與 DetailItem 的 `field=`——這也是「這一項改了沒」能自動
// 判定的原因：拿 changesSinceReturn 的 key 比對即可，不需要任何打勾狀態。

const FieldCommentsContext = createContext(null);

export const FieldCommentsProvider = FieldCommentsContext.Provider;

export const useFieldCommentsContext = () => useContext(FieldCommentsContext);

/**
 * 一則意見該顯示的欄位名。
 *
 * 存下來的 `label` 是寫入當下的快照，所以它**優先**——欄位日後改名或退場，
 * 三個月前那則意見仍要讀得懂。唯一的例外是快照本身就等於 key：那代表寫入時
 * 沒人報得出名字（例如 QC 逐題意見存成 `answers.q_specialists` 的舊資料），
 * 這時改用現在的對照表，把讀不懂的資料救回來。
 */
export const commentLabel = (c, labelFor) => (
  c.label && c.label !== c.field ? c.label : labelFor(c.field)
);

/** 最近一次退件留下的逐項意見（沒有就回空陣列）。 */
export const latestFieldComments = (events = []) => {
  const ev = (events || []).find((e) => e.action === 'rejected');
  return (ev && Array.isArray(ev.fieldComments)) ? ev.fieldComments : [];
};

/**
 * 頁面用的狀態機。
 *
 * @param events    Detail 頁已經載入給 History 的事件流（newest-first）
 * @param canComment 現在這個人能不能留意見＝他能不能退件（審核者、單子待審）
 * @param returned  單子是否正停在 awaiting_info——舊意見只在這個狀態顯示，
 *                  跟退回原因紅框的規則一致：重送或核准之後它就是歷史，
 *                  歷史的位置在 History（那裡永遠留著）。
 * @param amendment qca / car / ear 的 modified 事件帶的是整包 payload 而不是
 *                  編輯差異，判斷「改了沒」要走 amendmentChangesSinceReturn。
 * @param labels    { field: '顯示名' }——直接傳頁面的 CHANGE_LABELS。
 * @param expandChanges 把複合欄位拆成真正的列，跟頁面傳給 HistoryCard 的是
 *                  同一個函式。QC 的 answers 是一整包物件，不展開的話
 *                  `answers.q3` 這一則意見永遠標不出「已更新」——差異表裡
 *                  只有 `answers` 這個 key。
 */
export const useFieldComments = ({
  events = [], canComment = false, returned = false, amendment = false,
  labels: pageLabels = {}, expandChanges = null,
} = {}) => {
  const [drafts, setDrafts] = useState({});
  // 寫在哪一格就用那一格的顯示名。`labels` 這張對照表只涵蓋「會出現在 History
  // 差異表」的欄位，而可以留意見的地方更多——QC 的驗收表有幾十題，題目名字
  // 住在題庫裡而不是對照表裡。少了這一層，那些意見會存成 `answers.q_specialists`
  // 這種裸 key，申請者讀到的就是程式碼。
  const [inlineLabels, setInlineLabels] = useState({});
  // 同 HistoryCard / ChangesSinceReturnCard：附件那一列的名字由共用表提供，
  // 十六頁不必各寫一次 files: 'Attachments'。頁面自己的定義仍然蓋得過。
  const labels = useMemo(() => ({ ...SHARED_CHANGE_LABELS, ...pageLabels }), [pageLabels]);

  const existing = useMemo(
    () => (returned ? latestFieldComments(events) : []),
    [events, returned]
  );

  // 申請者退回後改了哪些欄位——用來把「已更新」標在對應的意見上。
  const changed = useMemo(() => {
    if (!returned && existing.length === 0) return {};
    const raw = (amendment ? amendmentChangesSinceReturn(events) : changesSinceReturn(events)) || {};
    return expandChanges ? expandChanges(raw) : raw;
  }, [events, amendment, returned, existing.length, expandChanges]);

  const existingByField = useMemo(() => {
    const map = {};
    existing.forEach((c) => {
      map[c.field] = {
        ...c,
        label: commentLabel(c, (f) => inlineLabels[f] || labels[f] || f),
        updated: Object.prototype.hasOwnProperty.call(changed, c.field),
      };
    });
    return map;
  }, [existing, changed, labels, inlineLabels]);

  // 顯示名的三段優先序：寫意見的那一格自己報的名字 → 頁面的 CHANGE_LABELS
  // → 裸 key（最後手段，代表兩邊都沒登記，總比空白好）。
  const nameOf = (field) => inlineLabels[field] || labels[field] || field;

  return useMemo(() => ({
    canComment,
    drafts,
    existing,
    existingByField,
    labels,
    labelFor: nameOf,
    // label 是選填的：DetailItem 與 FieldCommentAnchor 都知道自己那一格叫什麼，
    // 傳進來就不必再去對照表裡找（而題庫型的欄位根本不在對照表裡）。
    set: (field, text, label = null) => {
      setDrafts((d) => ({ ...d, [field]: text }));
      if (label) setInlineLabels((m) => (m[field] === label ? m : { ...m, [field]: label }));
    },
    remove: (field) => setDrafts((d) => {
      const next = { ...d };
      delete next[field];
      return next;
    }),
    clear: () => setDrafts({}),
    // 送給後端的形狀。空白的格子在這裡就丟掉（後端也會再濾一次）。
    list: () => Object.entries(drafts)
      .map(([field, comment]) => ({ field, label: nameOf(field), comment: (comment || '').trim() }))
      .filter((c) => c.comment),
  }), [canComment, drafts, existing, existingByField, labels, inlineLabels]); // eslint-disable-line react-hooks/exhaustive-deps
};

// ── 一則意見長什麼樣（唯讀）─────────────────────────────────────────────────
// 兩個地方共用：審核詳情頁那一格的底下，以及 Modify modal 裡對應的輸入框
// 底下。同一則意見在兩個畫面必須長得一樣，否則申請者會以為那是兩件事。
export const FieldCommentNote = ({ entry }) => (
  <div className={fieldCommentBox}>
    <div className="flex items-center gap-1.5">
      <span className={fieldCommentMeta}>Reviewer&rsquo;s comment</span>
      {entry.updated && <span className={fieldCommentUpdated}>Updated</span>}
    </div>
    <p className={fieldCommentText}>{entry.comment}</p>
  </div>
);

/**
 * 「這一格的審核意見」——貼在表單控制項底下。
 *
 * 逐項意見若只集中在 Modify modal 最上面一整塊，要改的時候得一邊往上
 * 捲對照、一邊往下捲修改。所以意見長在它講的那一格旁邊，改哪一格看哪一句。
 *
 * 沒有 provider（不在 workflow 的頁面）、或這一格沒有意見時**完全不渲染**，
 * 所以 FormKit 的 `Field` 可以無條件掛著它。
 */
export const FieldCommentInline = ({ field }) => {
  const fc = useFieldCommentsContext();
  if (!fc || !field) return null;
  const entry = fc.existingByField[field];
  if (!entry) return null;
  return <FieldCommentNote entry={entry} />;
};

export default useFieldComments;
